import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";
import TakeWebCamPicture from "./TakeWebCamPicture";
import { isValidPhone, isValidName } from "../../Services/validation/validationUtiles";
import personIconPng from "../../assets/images/person_icon.png";
import passportIconPng from "../../assets/images/passport_icon.png";
import adminServices from "../../Services/adminService";
import visaService from "../../Services/visaService";
import "../../assets/css/groupForm.css";
import config from "../../config";
import { photoPopUpHtml } from "./formUtils";
import { isFileImage, isFileSizeWithinLimit, isObjectNotEmpty, capitalizeFirstLetter } from "../../utils";

const formInputs = [
  { label: "שם מלא", name: "fullName" },
  { label: "טלפון", name: "phone", type: "tel" },
  { label: "כתובת מגורים", name: "address" },
  { label: "שם חברה", name: "companyName" },
  { label: "כתובת מקום עבודה", name: "workAddress" },
  { label: "מקצוע", name: "profession" },
  { label: "תאריך נחיתה", name: "arrivalDate", type: "date" },
  { label: "שדה נחיתה בוייטנאם", name: "arrivalAirport" },
  { label: "שם מלון בוייטנאם", name: "hotelName" }
];

const defaultFormData = {
  fullName: "",
  phone: "",
  address: "",
  companyName: "",
  workAddress: "",
  profession: "",
  arrivalDate: "",
  arrivalAirport: "",
  hotelName: "",
  pictureOfPerson: null,
  pictureOfPassport: null
};

function VietnamForm() {
  let { organizationName, destination, dateOfTrip, groupId, groupAgentName } = useParams();
  organizationName = decodeURIComponent(organizationName);
  destination = decodeURIComponent(destination);
  groupAgentName = decodeURIComponent(groupAgentName);

  const [formData, setFormData] = useState(defaultFormData);
  const [formatErrors, setFormatErrors] = useState({});
  const [takePictureType, setTakePictureType] = useState(null);
  const formQuestions = useRef();

  const fileInputs = [
    { label: "תמונת דרכון", icon: passportIconPng, name: "pictureOfPassport", ref: useRef() },
    { label: "תמונת פספורט", icon: personIconPng, name: "pictureOfPerson", ref: useRef() }
  ];

  const onChange = ({ target: { name, value } }) => {
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[name];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [name]: value });
  };

  const onChangeFile = ({ target: { name, files } }) => {
    const file = files?.[0];
    if (!file) return;
    if (!isFileImage(file)) {
      Swal.fire("אופס,", "PDF ניתן להעלות רק תמונות או קבצי", "error");
      return;
    }
    if (!isFileSizeWithinLimit(file)) {
      Swal.fire("אופס,", "גודל הקובץ גדול מדי. ניתן להעלות עד 2.5MB", "error");
      return;
    }
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[name];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [name]: file });
  };

  const savePictureFromCamera = (pictureType, imgSrc) => {
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[pictureType];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [pictureType]: imgSrc });
  };

  const onFileInputClick = async (name, inputRef) => {
    const { isConfirmed } = await Swal.fire({
      html: photoPopUpHtml(name),
      confirmButtonText: "להמשיך",
      cancelButtonText: "ביטול",
      showCancelButton: true,
      showCloseButton: true
    });
    if (!isConfirmed) return;
    if (!config.isMobile) {
      const userResponse = await Swal.fire({
        icon: "question",
        text: "האם תרצ/י להתצטלם בעזרת המצלמה המחוברת למחשב/לפטופ או להעלות קובץ תמונה מהמחשב?",
        confirmButtonText: "לצלם תמונה",
        cancelButtonText: "להעלות תמונה",
        showCancelButton: true,
        showCloseButton: true
      });

      if (userResponse.value) {
        setTakePictureType(name);
      } else if (userResponse.dismiss === "cancel") {
        inputRef.current.click();
      }
    } else {
      inputRef.current.click();
    }
  };

  const validateForm = (data) => {
    const formatErrors = {};

    if (!isValidName(data.fullName)) {
      formatErrors.fullName = "שם מלא לא תקין";
    }

    if (!isValidPhone(data.phone)) {
      formatErrors.phone = "מספר טלפון לא תקין";
    }

    if (!data.address) {
      formatErrors.address = "נא למלא כתובת";
    }

    if (!data.companyName) {
      formatErrors.companyName = "נא למלא שם חברה";
    }

    if (!data.workAddress) {
      formatErrors.workAddress = "נא למלא כתובת מקום עבודה";
    }

    if (!data.profession) {
      formatErrors.profession = "נא למלא מקצוע";
    }

    if (!data.arrivalDate) {
      formatErrors.arrivalDate = "נא למלא תאריך נחיתה";
    }

    if (!data.arrivalAirport) {
      formatErrors.arrivalAirport = "נא למלא שדה נחיתה";
    }

    if (!data.hotelName) {
      formatErrors.hotelName = "נא למלא שם מלון";
    }

    if (!data.pictureOfPassport) {
      formatErrors.pictureOfPassport = true;
    }

    if (!data.pictureOfPerson) {
      formatErrors.pictureOfPerson = true;
    }

    setFormatErrors(formatErrors);
    return formatErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formatErrors = validateForm(formData);
    if (isObjectNotEmpty(formatErrors)) {
      formQuestions.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const form = new FormData();
    for (const field in formData) {
      form.append(field, formData[field]);
    }
    form.append("groupId", groupId);

    const addParticipantRes = await adminServices.addParticipantToGroup(form);
    const newGroupVisaRes = await visaService.createNewGroupVisa({
      visaData: {
        ...formData,
        destination: capitalizeFirstLetter(destination),
        pictureOfPassport: addParticipantRes.data.pictureOfPassportFileName,
        pictureOfPerson: addParticipantRes.data.pictureOfPersonFileName
      }
    });

    if (addParticipantRes?.success === true && newGroupVisaRes?.success === true) {
      setFormData(defaultFormData);
      Swal.fire("בוצע בהצלחה", "הטופס נשלח בהצלחה", "success");
    }
  };

  return (
    <div>
      <Header groupAgentName={groupAgentName} organizationName={organizationName} dateOfTrip={dateOfTrip} destination={destination} />

      <form className="group-form-form" onSubmit={handleSubmit} ref={formQuestions}>
        <h2 className="group-form-title">הכל כמעט מוכן לבואכם</h2>

        <div className="group-form-inputs-container">
          {formInputs.map(({ label, name, type }) => (
            <label key={name} className="group-form-input-label">
              {label}:
              <input
                className="group-form-input"
                value={formData[name]}
                name={name}
                type={type || "text"}
                placeholder={label}
                onChange={onChange}
              />
              {formatErrors[name] && <p className="text-format-error">* {formatErrors[name]}</p>}
            </label>
          ))}
        </div>

        <div className="group-form-inputs-container mb-20">
          {fileInputs.map(({ label, icon, name, ref }) => (
            <div key={name} className="group-form-file-input-container">
              <img src={icon} alt="icon" />

              <span className="group-form-file-input-text">
                <p className="group-form-file-input-text-label">{label}</p>
                {formData[name] ? (
                  <p className="group-form-file-input-text-success">מסמך צורף</p>
                ) : (
                  <p className={"group-form-file-input-text-falsy " + (formatErrors[name] ? "file-input-error" : "")}>
                    שום מסמך לא צורף
                  </p>
                )}
              </span>

              <button
                className="group-form-file-input-button"
                type="button"
                onClick={() => {
                  onFileInputClick(name, ref);
                }}
              >
                {formData[name] ? <>&#10004;</> : "צרף"}
              </button>

              <input hidden ref={ref} name={name} type="file" onChange={onChangeFile} />
            </div>
          ))}
        </div>
        <button className="submit-button" type="submit" value="Submit">
          שלח
        </button>
      </form>

      <TakeWebCamPicture
        takePictureType={takePictureType}
        setTakePictureType={setTakePictureType}
        savePictureFromCamera={savePictureFromCamera}
      />
    </div>
  );
}

export default VietnamForm;
